import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useMetroMapDispatch, useMetroMapStore } from './useMetroMapStore'
import { buildVariantsSignature, load, save } from './routingVariantPersistence'

/**
 * Headless React component (returns `null`) that bridges the
 * `routeStatus` slice of the MetroMap store with the user's browser
 * localStorage so the previously-selected route variant is restored
 * when the user lands on a results state with the same variants set.
 *
 * **Restore policy.** When `routeStatus` transitions INTO `results`,
 * read the persisted entry. If its `variantsSignature` matches the
 * current routes' signature, clamp the persisted index to
 * `[0, variants.count-1]` and dispatch `RouteSelectedVariantChanged`
 * to apply it. If signatures diverge (different graph, different
 * From/To, different algorithm cut) — discard the persisted index and
 * leave the default 0 from the reducer; overwrite localStorage with
 * the new signature + index 0 so subsequent sessions match.
 *
 * **Save policy.** On every change to `selectedIndex` while in
 * `results`, write `{variantIndex, variantsSignature}` to
 * localStorage. This captures user-driven taps on
 * `RouteSelectedVariantChanged`.
 *
 * **Re-arm on transition out of results.** A `restoredOnceRef` tracks
 * the signature for which we have already applied a restore in the
 * current session. When `routeStatus` leaves the `results` kind we
 * reset the ref so the next results-arrival re-runs the restore
 * logic — necessary for a new routing request that happens to share
 * the previous signature, and for the From/To churn lifecycle that
 * routinely cycles `results → loading → results`.
 *
 * **Double-fire on first restore (harmless).** When a persisted index
 * differs from the default 0, the effect dispatches
 * `RouteSelectedVariantChanged`; the reducer applies it and the
 * `routeStatus` snapshot updates, re-running this effect. The second
 * pass takes the `restoredOnceRef.current === sig` branch and writes
 * the now-consistent index to localStorage — no infinite loop, no
 * extra dispatch, exactly one save.
 *
 * Mount point: inside `<MetroMapStoreProvider>` adjacent to the other
 * route-sync components in `<AppShell>`. Returns `null` because it
 * has no DOM presence — pure side-effect bridge.
 */
export function RoutingVariantSync(): null {
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)

  // Tracks the variants signature for which we have already applied a
  // restore (or written an initial entry) during this `results`
  // session. Reset to `null` whenever `routeStatus.kind !== 'results'`,
  // so the next results-arrival re-runs the restore branch.
  const restoredOnceRef = useRef<string | null>(null)

  useEffect(() => {
    if (routeStatus.kind !== 'results') {
      // Re-arm for the next results-arrival.
      restoredOnceRef.current = null
      return
    }
    const sig = buildVariantsSignature(routeStatus.routes)
    if (restoredOnceRef.current === sig) {
      // We've already done the first-pass restore (or initial save) for
      // this exact signature in this session. Subsequent re-runs of
      // this effect therefore reflect either (a) the reducer applying
      // our own restore dispatch — see "double-fire" comment above —
      // or (b) a user tap that mutated `selectedIndex`. Both deserve a
      // save: the index in the store is now the canonical truth.
      save({ variantIndex: routeStatus.selectedIndex, variantsSignature: sig })
      return
    }
    // First sighting of this signature in the current session — apply
    // restore policy.
    restoredOnceRef.current = sig
    const persisted = load()
    if (persisted !== null && persisted.variantsSignature === sig) {
      const clamped = Math.max(0, Math.min(routeStatus.routes.length - 1, persisted.variantIndex))
      if (clamped !== routeStatus.selectedIndex) {
        dispatch({ kind: 'RouteSelectedVariantChanged', index: clamped })
      }
      // No save needed — current state already matches what we'd write.
    } else {
      // Different (or missing) persisted signature → write current
      // state so the next session has an accurate snapshot.
      save({ variantIndex: routeStatus.selectedIndex, variantsSignature: sig })
    }
  }, [routeStatus, dispatch])

  return null
}
